import { useCallback, useEffect, useRef, useState } from "react";
import { LensPlatformClient, LensSDKException } from "lens-platform-sdk";
import { useLensPlatformClient } from "src/hooks/useLensPlatformClient";
import { useAnalytics } from "src/hooks/useAnalytics";

type ActionResponse = Awaited<ReturnType<LensPlatformClient["business"]["deleteBusinessUser"]>>;

export const useDeleteBusinessUser = () => {
  const lensPlatformClient = useLensPlatformClient();
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<ActionResponse>();
  const [error, setError] = useState<LensSDKException>();
  const { track, trackError } = useAnalytics();

  // Keep up with mounted/unmounted state
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const deleteBusinessUser = useCallback(
    async (businessId: string, businessUserId: string) => {
      let lensSDKException: LensSDKException | undefined;

      try {
        if (mounted.current) {
          setError(undefined);
          setLoading(true);
        }
        const response = await lensPlatformClient.business.deleteBusinessUser(businessId, businessUserId);

        if (mounted.current) {
          track("Remove business user");
          setResponse(response);
        }
      } catch (exception) {
        if (exception instanceof LensSDKException) {
          trackError("Remove business use failed");
          setError(exception);
          lensSDKException = exception;
        }
      } finally {
        if (mounted.current) {
          setLoading(false);
        }
      }

      return lensSDKException;
    },
    [lensPlatformClient.business, track, trackError],
  );

  return {
    deleteBusinessUser,
    error,
    loading,
    response,
  };
};
